import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, CONTACT_PHONE, WHATSAPP_CHANNEL_URL, trackEvent } from "@/lib/api";
import {
  CheckCircle2, Loader2, Phone, MessageCircle, ArrowUpRight,
  Smartphone, Banknote, CreditCard, Clock, Copy, Check, AlertCircle,
} from "lucide-react";

const METHOD_LABELS = {
  tikkie: { label: "Tikkie", icon: Smartphone },
  bank_transfer: { label: "Bankoverschrijving", icon: Banknote },
  credit_card: { label: "Creditcard", icon: CreditCard },
};

const OPTION_LABELS = {
  pay_in_full: { label: "In één keer betalen", note: "Betaal het volledige cursusbedrag om je plek te reserveren." },
  deposit_remaining: { label: "Aanbetaling + Restbedrag", note: "Betaal vandaag een aanbetaling om je plek te reserveren. Het resterende bedrag moet vóór de cursusstart worden voldaan." },
  payment_plan: { label: "Betalingsregeling", note: "Betaal de eerste termijn nu. We nemen contact op over termijn 2 en 3." },
};

const STATUS_MAP = {
  pending_payment: { label: "Betaling in afwachting", color: "bg-yellow-200 text-yellow-900 border-yellow-300", paid: false },
  not_paid: { label: "Niet betaald", color: "bg-red-200 text-red-900 border-red-300", paid: false },
  deposit_paid: { label: "Aanbetaling voldaan", color: "bg-blue-200 text-blue-900 border-blue-300", paid: true },
  payment_plan: { label: "Betalingsregeling", color: "bg-blue-200 text-blue-900 border-blue-300", paid: false },
  fully_paid: { label: "Volledig betaald", color: "bg-green-200 text-green-900 border-green-300", paid: true },
};

function CopyButton({ value, testId }) {
  const [copied, setCopied] = useState(false);
  const handle = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn("Clipboard write failed:", err);
    }
  };
  return (
    <button
      type="button"
      onClick={handle}
      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#FF6A1A] hover:opacity-80"
      data-testid={testId}
    >
      {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
    </button>
  );
}

export default function Confirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    trackEvent("confirmation_page_view", { order_id: orderId });
    (async () => {
      try {
        const [orderRes, bankRes] = await Promise.all([
          api.get(`/orders/${orderId}`),
          api.get(`/bank-details`),
        ]);
        if (!cancelled) {
          setOrder(orderRes.data);
          setBank(bankRes.data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load confirmation data:", err);
          setOrder(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  if (loading) {
    return (
      <Layout step={3}>
        <div className="pt-20 flex justify-center">
          <Loader2 className="animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout step={3}>
        <p className="pt-10 text-sm">We konden je aanmelding niet vinden.</p>
        <Link to="/" className="underline text-sm">Naar de homepage</Link>
      </Layout>
    );
  }

  const method = METHOD_LABELS[order.payment_method] || null;
  const option = OPTION_LABELS[order.payment_option] || null;
  const MethodIcon = method?.icon || Clock;
  const status = STATUS_MAP[order.payment_status_manual] || STATUS_MAP.pending_payment;
  const isPaid = !!status.paid;
  const tikkieUrl = order.payment_url;
  const isBankTransfer = order.payment_method === "bank_transfer";
  const reference = `${bank?.reference_prefix || "PTRACADAMY"} ${order.order_id}`;
  const due = Number(order.payment_due || 0);

  return (
    <Layout step={3}>
      <div className="pt-2">
        <div className="flex items-center gap-3 label-tag" data-testid="status-received">
          <CheckCircle2 size={20} />
          <span>— Aanmelding ontvangen</span>
        </div>
        <h1 className="mt-4 font-display text-5xl sm:text-7xl leading-[0.95] tracking-tight">
          {isPaid ? <>Welkom,<br /><span className="italic">{order.first_name}</span></> : <>Bedankt,<br /><span className="italic">{order.first_name}</span></>}
        </h1>

        {!isPaid && (
          <div
            className="mt-5 rounded-2xl bg-black/90 text-[#FF6A1A] px-5 py-4 flex items-start gap-3 text-sm font-medium leading-snug"
            data-testid="pending-payment-banner"
          >
            <AlertCircle size={20} className="shrink-0 mt-0.5" />
            <div>
              Je aanmelding is ontvangen. Rond nu je betaling af om je plek te reserveren.
              <span className={`mt-2 inline-block px-2.5 py-1 rounded-full text-[11px] font-bold border ${status.color}`} data-testid="status-badge">
                {status.label}
              </span>
            </div>
          </div>
        )}

        {/* Course summary */}
        <div className="mt-8 glass-block" data-testid="course-summary">
          <div className="label-tag opacity-80">Cursus</div>
          <div className="font-display text-3xl sm:text-4xl mt-1 leading-tight text-[#FF6A1A]">{order.course_name}</div>

          <div className="mt-5 pt-4 border-t border-[#FF6A1A]/25 flex items-end justify-between gap-3">
            <div>
              <div className="label-tag opacity-80">Totale cursusprijs</div>
              <div className="text-[#FF6A1A]/85 text-base font-semibold mt-0.5">
                €{Number(order.amount).toLocaleString("en-US")}
              </div>
            </div>
            {due > 0 && (
              <div className="text-right">
                <div className="label-tag opacity-80">{isPaid ? "Betaald" : "Nu te betalen"}</div>
                <div className="font-display text-4xl text-[#FF6A1A] leading-none" data-testid="payment-due">
                  €{due.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment action area */}
        {!isPaid && (
          <div className="mt-6 glass-block" data-testid="payment-action">
            {tikkieUrl && (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <MethodIcon size={22} strokeWidth={1.7} className="text-[#FF6A1A]" />
                  <div className="label-tag text-[#FF6A1A] opacity-90">Rond je betaling af</div>
                </div>
                <p className="text-[#FF6A1A]/95 text-sm font-medium leading-relaxed mb-4">
                  Klik op de knop om €{due.toFixed(2)} veilig te betalen via Tikkie. Tikkie ondersteunt ook creditcard betalingen.
                </p>
                <a
                  href={tikkieUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#FF6A1A] text-black font-bold px-5 py-4 rounded-full text-base hover:opacity-90 transition-opacity shadow-[0_8px_20px_-6px_rgba(255,106,26,0.55)]"
                  data-testid="pay-with-tikkie-button"
                >
                  <Smartphone size={18} strokeWidth={2.2} />
                  <span>Betaal met Tikkie · €{due.toFixed(2)}</span>
                  <ArrowUpRight size={16} strokeWidth={2.2} />
                </a>
                <p className="mt-3 text-[11px] text-[#FF6A1A]/70 text-center font-medium tracking-wide uppercase">
                  Je plek is gereserveerd zodra de betaling is ontvangen
                </p>
              </>
            )}

            {isBankTransfer && bank && (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <Banknote size={22} strokeWidth={1.7} className="text-[#FF6A1A]" />
                  <div className="label-tag text-[#FF6A1A] opacity-90">Bankoverschrijving gegevens</div>
                </div>
                <p className="text-[#FF6A1A]/95 text-sm font-medium leading-relaxed mb-4">
                  Maak €{due.toFixed(2)} over met de gegevens hieronder. Gebruik de referentie exact zodat we je betaling kunnen koppelen.
                </p>
                <div className="space-y-3" data-testid="bank-details">
                  <BankRow label="Bedrag" value={`€${due.toFixed(2)}`} testId="bank-amount" />
                  <BankRow label="IBAN" value={bank.iban} testId="bank-iban" copy />
                  <BankRow label="Tenaamstelling" value={bank.account_holder} testId="bank-holder" />
                  <BankRow label="Referentie" value={reference} testId="bank-reference" copy />
                </div>
                <p className="mt-4 text-[11px] text-[#FF6A1A]/70 font-medium tracking-wide uppercase text-center">
                  Je plek is gereserveerd zodra je overschrijving binnen is (meestal binnen 1-2 werkdagen).
                </p>
              </>
            )}
          </div>
        )}

        {isPaid && (
          <div className="mt-6 glass-block" data-testid="paid-banner">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={22} className="text-[#FF6A1A]" />
              <div>
                <div className="label-tag text-[#FF6A1A] opacity-90">Betaling bevestigd</div>
                <p className="mt-1 text-[#FF6A1A]/95 text-sm font-medium leading-relaxed">
                  Je bent officieel ingeschreven! Je plek is gereserveerd.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Payment summary */}
        {(method || option) && (
          <div className="mt-6 glass-block" data-testid="payment-summary">
            <div className="label-tag text-[#FF6A1A] opacity-90 mb-4">Jouw betalingskeuze</div>

            {option && (
              <div className="mb-4">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#FF6A1A]/70">Betaaloptie</div>
                <div className="mt-1 text-[#FF6A1A] font-semibold text-lg" data-testid="option-label">{option.label}</div>
                <p className="mt-1 text-[#FF6A1A]/85 text-sm font-medium leading-relaxed">{option.note}</p>
              </div>
            )}

            {method && (
              <div className="pt-4 border-t border-[#FF6A1A]/25">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#FF6A1A]/70">Betaalmethode</div>
                <div className="mt-1 flex items-center gap-2 text-[#FF6A1A] font-semibold text-lg" data-testid="method-label">
                  <MethodIcon size={18} strokeWidth={1.8} />
                  {method.label}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Next steps */}
        <div className="mt-6 glass-block">
          <div className="label-tag text-[#FF6A1A] opacity-90 mb-3">↳ Wat gebeurt er nu?</div>
          <ul className="text-[#FF6A1A]/95 text-sm font-medium leading-relaxed space-y-1.5">
            <li>— Je ontvangt een e-mail met je aanmelding + betaalgegevens.</li>
            <li>— Je plek is officieel gereserveerd zodra je betaling is ontvangen.</li>
            <li>— De exacte locatie en details krijg je dichter bij de cursusdatum.</li>
          </ul>
        </div>

        {/* WhatsApp Channel CTA */}
        <div className="mt-6 glass-block">
          <div className="flex items-center gap-3 mb-3">
            <MessageCircle size={22} strokeWidth={1.7} className="text-[#FF6A1A]" />
            <div className="label-tag text-[#FF6A1A] opacity-90">Join ons WhatsApp kanaal</div>
          </div>
          <p className="text-[#FF6A1A]/95 text-sm font-medium leading-relaxed">
            Blijf op de hoogte en ontvang alle belangrijke informatie over je cursus.
          </p>
          <a
            href={WHATSAPP_CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 bg-[#FF6A1A] text-black font-semibold px-5 py-3 rounded-full text-sm hover:opacity-90 transition-opacity shadow-[0_8px_20px_-6px_rgba(255,106,26,0.45)]"
            data-testid="whatsapp-channel-link"
          >
            <MessageCircle size={16} strokeWidth={2.2} />
            <span>Naar WhatsApp kanaal</span>
            <ArrowUpRight size={14} strokeWidth={2.2} />
          </a>
        </div>

        <div className="mt-12 border-t border-black/30 pt-6">
          <p className="text-sm font-medium">
            Heb je vragen? Neem gerust direct contact met me op.
          </p>
          <div className="mt-3 flex items-center gap-2 text-sm">
            <Phone size={14} />
            <span className="font-semibold">{CONTACT_PHONE}</span>
          </div>
        </div>

        <Link
          to="/"
          className="mt-10 inline-block label-tag underline hover:opacity-70"
          data-testid="home-link"
        >
          ← Terug naar cursussen
        </Link>
      </div>
    </Layout>
  );
}

function BankRow({ label, value, testId, copy = false }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-[#FF6A1A]/10 border border-[#FF6A1A]/25 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#FF6A1A]/70">{label}</div>
        <div className="mt-0.5 text-[#FF6A1A] font-semibold text-sm break-words" data-testid={testId}>{value}</div>
      </div>
      {copy && <CopyButton value={value} testId={`${testId}-copy`} />}
    </div>
  );
}
